import { isFontPresetKey, type FontPresetKey } from './fontPresets';
import { darkColors, lightColors, type ThemeColors, type ThemeName } from './tokens';

/**
 * Tenant branding applied at runtime — a port of `patient-app/src/theme/brand.ts`
 * so the portal derives exactly the palette the native app does from the same
 * `/portal/config` / `/portal/branding` payload. Status colours are never
 * derived from seeds (the server rejects them; this factory never maps them).
 */

export type BrandPaletteSeeds = {
  primary: string;
  onPrimary: string;
  accent: string;
  /**
   * A second hue, far enough from the anchor to survive on it. Tenants whose
   * anchor and accent share a hue family (petrol + teal measures 2.5:1) have no
   * brand colour available on any dark surface, which is what makes those
   * palettes read as neutral navy. `action` is the way out: it carries the
   * primary button AND the attention state, so the palette gets smaller rather
   * than busier — a patient learns one "look at this" colour. Absent, nothing
   * here changes: the button keeps the anchor and attention keeps the amber,
   * which is what every stored palette renders today.
   */
  action?: string;
  /**
   * The accent's legible form on the anchor, for labels only. Absent, it is
   * derived by lifting the accent toward white — the mix this file already
   * performed inline before the seed existed.
   */
  accentOnDark?: string;
  surfaceHigh?: string;
};

export type BrandPalette = { light: BrandPaletteSeeds; dark: BrandPaletteSeeds };

export type BrandAttribution = { mode: 'SHOW' | 'HIDE'; platformName: string | null };

export type Brand = {
  version: number;
  displayName: string;
  palette: BrandPalette;
  logoUrls: { light: string | null; dark: string | null };
  attribution: BrandAttribution;
  fontPreset?: FontPresetKey;
  brandId: string | null;
};

const HEX_PATTERN = /^#[0-9a-fA-F]{6}$/;

function isSeeds(value: unknown): value is BrandPaletteSeeds {
  if (!value || typeof value !== 'object') return false;
  const seeds = value as Record<string, unknown>;
  const required = [seeds.primary, seeds.onPrimary, seeds.accent];
  if (!required.every((c) => typeof c === 'string' && HEX_PATTERN.test(c))) return false;
  const optional = [seeds.surfaceHigh, seeds.action, seeds.accentOnDark];
  return optional.every((c) => c === undefined || (typeof c === 'string' && HEX_PATTERN.test(c)));
}

export function isBrand(value: unknown): value is Brand {
  if (!value || typeof value !== 'object') return false;
  const brand = value as Record<string, any>;
  return (
    typeof brand.version === 'number' &&
    typeof brand.displayName === 'string' &&
    Boolean(brand.palette) &&
    isSeeds(brand.palette.light) &&
    isSeeds(brand.palette.dark) &&
    Boolean(brand.logoUrls) &&
    (brand.logoUrls.light === null || typeof brand.logoUrls.light === 'string') &&
    (brand.logoUrls.dark === null || typeof brand.logoUrls.dark === 'string') &&
    Boolean(brand.attribution) &&
    (brand.attribution.mode === 'SHOW' || brand.attribution.mode === 'HIDE') &&
    (brand.attribution.platformName === null || typeof brand.attribution.platformName === 'string') &&
    (brand.brandId === null || typeof brand.brandId === 'string')
  );
}

/** Maps an API branding payload to a Brand, resolving server-relative logo paths. */
export function brandFromApi(payload: unknown, apiBase: string): Brand | null {
  if (!payload || typeof payload !== 'object') return null;
  const raw = payload as Record<string, any>;
  const absolute = (url: unknown) =>
    typeof url === 'string' && url ? (url.startsWith('http') ? url : `${apiBase}${url}`) : null;
  const candidate = {
    ...raw,
    fontPreset: isFontPresetKey(raw.fontPreset) ? raw.fontPreset : undefined,
    logoUrls: { light: absolute(raw.logoUrls?.light), dark: absolute(raw.logoUrls?.dark) },
  };
  return isBrand(candidate) ? candidate : null;
}

/** The logo for the active scheme; dark falls back to light. */
export function brandLogoUrl(brand: Brand | null, scheme: ThemeName): string | null {
  if (!brand) return null;
  return (scheme === 'dark' ? brand.logoUrls.dark ?? brand.logoUrls.light : brand.logoUrls.light) ?? null;
}

function hexToRgb(hex: string): [number, number, number] {
  const v = hex.replace('#', '');
  return [parseInt(v.slice(0, 2), 16), parseInt(v.slice(2, 4), 16), parseInt(v.slice(4, 6), 16)];
}

function toHexChannel(value: number) {
  return Math.round(Math.min(255, Math.max(0, value))).toString(16).padStart(2, '0');
}

/** Blends `base` towards `target` by `weight` (0..1). Hex in, hex out. */
export function mixHex(base: string, target: string, weight: number): string {
  const [r1, g1, b1] = hexToRgb(base);
  const [r2, g2, b2] = hexToRgb(target);
  return `#${toHexChannel(r1 + (r2 - r1) * weight)}${toHexChannel(g1 + (g2 - g1) * weight)}${toHexChannel(b1 + (b2 - b1) * weight)}`;
}

function luminance(hex: string): number {
  const [r, g, b] = hexToRgb(hex).map((v) => {
    const c = v / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

function contrastRatio(a: string, b: string): number {
  const [hi, lo] = [luminance(a), luminance(b)].sort((x, y) => y - x);
  return (hi + 0.05) / (lo + 0.05);
}

/**
 * Whichever of white or near-black is more legible on `background`, by measured
 * WCAG ratio rather than a lightness threshold — the two disagree exactly in
 * the mid-tone band where an action colour tends to sit.
 */
const BUTTON_INK = '#0A0A0C';
function onColorFor(background: string): string {
  return contrastRatio(background, '#FFFFFF') >= contrastRatio(background, BUTTON_INK)
    ? '#FFFFFF'
    : BUTTON_INK;
}

/**
 * Full semantic colour set for a scheme with a tenant palette overlaid.
 * `palette: null` returns the defaults untouched — the unbranded portal is
 * byte-for-byte the Elenthor design.
 *
 * The derivation follows the brand-system structure a tenant is actually
 * given (Fakih spec, 26 Aug 2026): ONE dark anchor that every dark surface
 * shares — rail, hero, cryostorage monitor, primary buttons — and ONE
 * accent that appears only on interactive and brand moments. Variety comes
 * from layout, not hue, so the rail is not "a darker version" of the hero:
 * it is the same value. The dark scheme is built from that same anchor —
 * grounds sink into the anchor's hue rather than staying Elenthor aubergine,
 * so a petrol clinic's night mode is petrol-black, not purple-black.
 */
export function buildColors(scheme: ThemeName, palette: BrandPalette | null): ThemeColors {
  const base = scheme === 'dark' ? darkColors : lightColors;
  if (!palette) return base;
  const seeds = palette[scheme];
  const isDark = scheme === 'dark';
  const hoverTarget = isDark ? '#FFFFFF' : '#000000';
  // The anchor is the LIGHT primary in both schemes: it is the brand's dark
  // surface, and a dark scheme's own primary is a pale action tint.
  const anchor = palette.light.surfaceHigh ?? palette.light.primary;
  const black = '#050709';
  // The action hue, for the seeds that name one; without it there is no second
  // hue to reach for and every consumer here reads the accent instead.
  const action = seeds.action ?? seeds.accent;
  // The filled button. Seeding `action` is what splits it away from the anchor,
  // so a primary button stops being another navy slab and becomes something you
  // can find on the page. Unseeded it stays on `primary` — NOT on the accent —
  // because that is the colour every stored palette's buttons render today, and
  // an existing tenant must not have their buttons change hue underneath them.
  const button = seeds.action ?? seeds.primary;
  // Buttons and attention share one hue by design, so the text on the button
  // has to be measured against whichever hue actually landed on it: the
  // tenant's `onPrimary` answers for the anchor, and once the button is no
  // longer the anchor that answer is a coin flip.
  const onButton = seeds.action ? onColorFor(button) : seeds.onPrimary;

  if (isDark) {
    // Grounds: three steps of the anchor sunk toward black, so cards still
    // read as raised. The hero keeps the anchor itself — the one surface in
    // the dark scheme allowed to sit at full brand value.
    const surface = mixHex(anchor, black, 0.72);
    const raised = mixHex(anchor, black, 0.56);
    const raisedHigh = mixHex(anchor, black, 0.4);
    const hero = seeds.surfaceHigh ?? mixHex(anchor, black, 0.12);
    // A dark scheme's accent is already a pale tint, so the seeded value is
    // preferred but the fallback only nudges it toward white — the light
    // scheme's 0.35 lift would wash it out against these grounds.
    const accentOnDark = seeds.accentOnDark ?? mixHex(seeds.accent, '#FFFFFF', 0.1);
    return {
      ...base,
      surface,
      surfaceLowest: raised,
      surfaceLow: raisedHigh,
      surfaceHigh: hero,
      surfaceGlass: raised,
      inputSurface: raisedHigh,
      surfacePanel: mixHex(anchor, black, 0.64),
      surfaceHead: mixHex(anchor, black, 0.5),
      onSurface: mixHex('#F1EFF6', seeds.accent, 0.06),
      onSurfaceVariant: mixHex('#9A94A6', seeds.accent, 0.2),
      onSurfaceFaint: mixHex('#6C6678', seeds.accent, 0.2),
      onInverse: '#FFFFFF',
      onInverseVariant: accentOnDark,
      primary: button,
      primaryHover: mixHex(button, hoverTarget, 0.12),
      primaryContainer: mixHex(seeds.accent, surface, 0.8),
      primaryContainerSoft: mixHex(seeds.accent, surface, 0.88),
      onPrimary: onButton,
      accent: seeds.accent,
      accentDeep: mixHex(seeds.accent, hoverTarget, 0.2),
      info: seeds.accent,
      infoContainer: mixHex(seeds.accent, surface, 0.8),
      // Attention folds into the action hue only when the tenant has actually
      // chosen one; without it the amber stays, because an accent-coloured
      // "warning" would be indistinguishable from every link on the page.
      // success/error are never derived — a tenant may not restyle safety.
      ...(seeds.action
        ? { warning: action, warningContainer: mixHex(action, surface, 0.8) }
        : null),
      border: 'rgba(255, 255, 255, 0.09)',
      borderStrong: 'rgba(255, 255, 255, 0.16)',
      divider: 'rgba(255, 255, 255, 0.06)',
      rail: mixHex(anchor, black, 0.62),
      railRaised: 'rgba(255, 255, 255, 0.08)',
      railText: '#FFFFFF',
      railTextMuted: accentOnDark,
      railTextFaint: mixHex('#FFFFFF', mixHex(anchor, black, 0.62), 0.42),
      railHairline: 'rgba(255, 255, 255, 0.07)',
      railAccent: seeds.accent,
      onInverseHairline: 'rgba(255, 255, 255, 0.14)',
      onInverseFill: 'rgba(255, 255, 255, 0.10)',
      onInverseFillHover: 'rgba(255, 255, 255, 0.18)',
      skeleton: raisedHigh,
      scrim: 'rgba(0, 0, 0, 0.6)',
    };
  }

  const surface = mixHex(base.surface, anchor, 0.02);
  // "Accent on dark": labels inside dark cards are the accent lifted toward
  // white (#0A7596 → ~#5FA3BB), the same hue links carry on light surfaces.
  // A tenant who has measured their own value on the anchor overrides the mix.
  const accentOnDark = seeds.accentOnDark ?? mixHex(seeds.accent, '#FFFFFF', 0.35);
  // The rail shares the anchor's value when the anchor is dark enough to
  // carry white text at 10:1+; a mid-tone primary is sunk instead so the
  // rail never becomes a bright slab.
  //
  // Pinning `surfaceHigh` opts out of that sinking. It is the seed that says
  // "this exact value IS my dark surface", so a brand built ON a mid-tone —
  // a clinic whose logo teal is the product's colour, not an accent it
  // carries — keeps its own value here instead of being darkened into a hue
  // it never chose. The seed still has to clear the contrast gate to be
  // stored, so opting in cannot produce an illegible rail.
  const rail = seeds.surfaceHigh ?? (luminance(anchor) < 0.06 ? anchor : mixHex(anchor, black, 0.55));
  return {
    ...base,
    surface,
    surfaceLow: mixHex(base.surfaceLow, anchor, 0.02),
    surfacePanel: mixHex(base.surfacePanel, anchor, 0.01),
    surfaceHead: mixHex(base.surfaceHead, anchor, 0.015),
    border: mixHex(base.border, anchor, 0.03),
    borderStrong: mixHex(base.borderStrong, anchor, 0.04),
    divider: mixHex(base.divider, anchor, 0.02),
    onSurface: mixHex(base.onSurface, anchor, 0.12),
    onSurfaceStrong: mixHex(base.onSurfaceStrong, anchor, 0.12),
    onSurfaceVariant: mixHex(base.onSurfaceVariant, anchor, 0.1),
    onSurfaceFaint: mixHex(base.onSurfaceFaint, anchor, 0.1),
    primary: button,
    primaryHover: mixHex(button, hoverTarget, 0.12),
    // The brand tint (avatar circles, plan chips, selected-row fill) is a
    // wash of the ACCENT — an anchor washed to 12% reads as grey.
    primaryContainer: mixHex(seeds.accent, surface, 0.88),
    primaryContainerSoft: mixHex(seeds.accent, surface, 0.94),
    onPrimary: onButton,
    accent: seeds.accent,
    accentDeep: mixHex(seeds.accent, hoverTarget, 0.2),
    surfaceHigh: seeds.surfaceHigh ?? seeds.primary,
    onInverse: seeds.surfaceHigh ? base.onInverse : seeds.onPrimary,
    onInverseVariant: accentOnDark,
    info: seeds.accent,
    infoContainer: mixHex(seeds.accent, surface, 0.88),
    // See the dark branch: attention rides the action hue, and only when the
    // tenant seeded one. success/error stay untouched in both schemes.
    ...(seeds.action
      ? { warning: action, warningContainer: mixHex(action, surface, 0.88) }
      : null),
    rail,
    railText: '#FFFFFF',
    railTextMuted: accentOnDark,
    // De-emphasised rail text (the group titles, "Powered by") is a DIMMED
    // WHITE, not a tinted accent. Mixing the accent into the rail collapses
    // whenever the accent shares the anchor's hue — Fakih's teal lands at
    // 1.51:1 there and the group titles disappear. Pulling white 42% toward
    // the rail holds 5.2:1+ on every brand in the catalogue.
    railTextFaint: mixHex('#FFFFFF', rail, 0.42),
    railAccent: mixHex(seeds.accent, '#FFFFFF', 0.2),
  };
}
